import React from 'react';
import { Tooltip, Stack } from '@mui/material';
import { Archive, CalendarMonth, Done, Loop, Schedule, Search, Warning } from '@mui/icons-material';
import { ReleaseType } from '../__generated__/graphql';
import { formatDateOnly } from '../Formatters';

type ReleaseLike = { type?: ReleaseType | null } | null | undefined;
type NoticeLike = { deletedAt?: unknown | null; notice?: string | null; createdAt?: string | null } | null | undefined;
type OngoingRevisionLike = { id?: unknown } | null | undefined;

export type GuideStatusInfo = {
  releases?: ReleaseLike[] | null;
};

export type GuideTagsInfo = GuideStatusInfo & {
  hasChangesSinceLastPublish?: boolean | null;
  ongoingRevision?: OngoingRevisionLike;
  isRevisionPlanned?: boolean | null;
  isRevisionAssesmentInProgress?: boolean | null;
  notices?: NoticeLike[] | null;
};

export type GuideStatusLabel = 'Aktiv' | 'Ny' | 'Tilbaketrukket';

export function getGuideStatusLabel(guide: GuideStatusInfo): GuideStatusLabel {
  const releases = guide.releases ?? [];
  if (releases.length === 0) return 'Ny';
  if (releases[0]?.type === ReleaseType.Archive) return 'Tilbaketrukket';
  return 'Aktiv';
}

export function StatusChip({ guide }: { guide: GuideStatusInfo }) {
  switch (getGuideStatusLabel(guide)) {
    case 'Ny':
      return (
        <Tooltip title="Ny">
          <Schedule sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    case 'Tilbaketrukket':
      return (
        <Tooltip title="Tilbaketrukket">
          <Archive sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Aktiv">
          <Done sx={{ color: 'success.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
  }
}

export function NoticeOutlineIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-110 -110 1040 1040"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={70}
      strokeLinejoin="round"
      strokeLinecap="round">
      <path d="M28 26 L28 786 L620 786 L788 618 L788 26 Z" />
      <path d="M620 786 L620 618 L788 618" />
      <path d="M28 152 L788 152" />
    </svg>
  );
}

function NoticeTooltipContent({ notices }: { notices: NonNullable<NoticeLike>[] }) {
  return (
    <Stack spacing={1}>
      {notices.map((n, i) => (
        <span key={i} style={{ whiteSpace: 'pre-line' }}>
          {n.createdAt && <em>{formatDateOnly(n.createdAt)}: </em>}
          {n.notice}
        </span>
      ))}
    </Stack>
  );
}

export function TagsCell({ guide }: { guide: GuideTagsInfo }) {
  const releases = guide.releases ?? [];
  const hasRelease = releases.length > 0;
  const notices = (guide.notices ?? []).filter((n): n is NonNullable<NoticeLike> => !!n && !n.deletedAt);
  const noticeCount = notices.length;
  const noticesWithText = notices.filter((n) => !!n.notice);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
      {guide.hasChangesSinceLastPublish && hasRelease && (
        <Tooltip title="Upubliserte endringer">
          <Warning fontSize="small" sx={{ color: 'warning.main' }} />
        </Tooltip>
      )}
      {guide.ongoingRevision && (
        <Tooltip title="Under revisjon">
          <Loop fontSize="small" />
        </Tooltip>
      )}
      {guide.isRevisionPlanned && (
        <Tooltip title="Planlagt">
          <CalendarMonth fontSize="small" />
        </Tooltip>
      )}
      {guide.isRevisionAssesmentInProgress && (
        <Tooltip title="Kartlegging pågår">
          <Search fontSize="small" />
        </Tooltip>
      )}
      {noticeCount > 0 && (
        <Tooltip
          title={
            noticesWithText.length > 0 ? (
              <NoticeTooltipContent notices={noticesWithText} />
            ) : (
              `${noticeCount} ${noticeCount === 1 ? 'merknad' : 'merknader'}`
            )
          }>
          <span style={{ color: '#fbc02d', display: 'inline-flex', alignItems: 'center' }}>
            <NoticeOutlineIcon />
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}
